export default `---
slug: poland
country: Poland
countryCode: POL
continent: Europe
year: 2016
title: "Blessed, Hammered, and Holding an Australian Flag"
description: "World Youth Day in Krakow, 2016. Pope Francis, two crates of beer, a Fiat doing donuts, and the great flag heist."
thumbnail: /images/travels/POL/cover.jpg
relatedLinks: []
---

Summer 2016. The Catholic Church organises World Youth Day every few years — a gathering of young people from around the globe, with the Pope attending to do the mass. That year it was Krakow, Poland. My parish organised a trip, and I went.

Let me be honest about my motivation: I was still hanging around the parish mainly because my friends were there and we had a good group. The faith was present, but the friends were the real draw. <i>Same group, same excuses, great trip.</i>

## The Host Family

We got on a bus in northern Italy and drove for an extremely long time in the way that only teenagers on an overnight coach can — chaotically, loudly, barely sleeping, and not minding at all.

We arrived in the evening and were collected by the uncle of our host family, who brought me and my two best friends and another guy back to the house. We dropped our bags. Before we'd fully processed where we were, the uncle gestured toward the door.

"You want some beer?"

<b>We went to the supermarket. We came back with two crates.</b>

The next day, without prompting, he brought two more. That's the kind of welcome that leaves a mark.

Every evening for the whole week, we'd come back from whatever activities the day had brought — walks, church events, hanging with other groups, wandering around — and find a full table of home-cooked Polish food waiting for us. Hot, abundant, traditional. Bigos, pierogi, żurek, things I couldn't name but ate gratefully. <i>We were fed like royalty every single night.</i>

Then we'd open the crates and the homemade vodka and the liquor the family produced themselves, and we'd sit and eat and drink and talk until it was late. Perfect.

## The Father's Car

The father of the family had a Fiat 500 — one of the old ones, small as a shoebox, engine souped up well beyond what a car that size deserves, with a sound system installed that belonged in a stadium.

He would drive us to the meeting points for events, and here is what happened every single time: he'd pull up to the gathering, <b>music at full volume</b>, drop us off, and before driving away — donuts. Just full donuts in the car park, tyres screaming, while everyone nearby turned to stare.

We got out looking like absolute legends every time. It was magnificent.

## One Hundred Thousand People and Pope Francis

The main event was held in a massive park just outside Krakow. There were over 100,000 young people gathered in that field — from every country, every background, all there for the same thing. The night before the mass, we slept on the grass on air mattresses and in sleeping bags under the open sky. We barely slept, of course. The teenager-to-sleep ratio at these events is very unfavourable.

The next day, Pope Francis came. And I was close — genuinely close. I made a video on my phone as he passed, and at exactly the right moment, he turned in my direction and gave the blessing. Just a brief gesture, the kind he was making constantly as he moved through the crowd, but it was directed at me.

<b>When I showed my grandmother the video, she almost cried.</b>

That reaction, honestly, meant more to me than the moment itself.

## The Flag Market

World Youth Day has a tradition: everyone brings their national flag and trades with people from other countries. The symbolism is nice — borders dissolved, everyone just human beings making an exchange.

In practice, it is also a perfectly functioning supply and demand market.

Italian flags were worthless. There were so many of us, the field was flooded with Italian flags. Same story with Spain. But Canadian flags? Australian flags? Almost nobody had one. You could trade an Italian flag for a Spanish one — basically one-for-one, both oversupplied. You could get a Ukrainian flag for an Italian flag plus a bit of effort. But an Australian flag? <i>An Australian flag required either serious trading skill or extraordinary luck.</i>

I had a plan. I started trading up — Italy to something better, something better to Ukraine, Ukraine to something better again. And then, somewhere in the middle of the field, I found an Australian guy with his flag.

I just asked. Directly and cheerfully, as you have to do with Australians. He said yes.

The Australian flag is hanging in my childhood room at my parents' house, next to the Nepalese one. It represents a country I've never been to, won in a flag market in Poland through sheer determination, and is one of my favourite objects in that room.

<b>I've never been to Australia. I would love to go. The flag will be waiting when I do.</b>
`;
